// «Invitar» in a call — the pure planner of shared/API.md §30.3 (twins: Swift CallInvitePlan,
// Kotlin CallInvitePlan, C# CallInvitePlan; one test table, same case names).
//
// The Calls plugin has NO invite route: a call belongs to one channel and only its members can see
// or join it, and a DM can never gain a third member. So an invitation is built from ordinary REST
// and decided here, from facts the client already holds — never by trying and reading the error:
//
// | Conversation   | Invitees                               | Plan                                   |
// |----------------|----------------------------------------|----------------------------------------|
// | O / P channel  | all members                            | ring(invitees)                         |
// | O / P channel  | some not members, canAddMembers        | addThenRing(toAdd, ring = all)         |
// | O / P channel  | some not members, NOT canAddMembers    | notAllowed(those) — nothing is sent    |
// | G group        | all members                            | ring(invitees)                         |
// | D, or G + any outsider                                  | moveToGroup(members ∪ invitees, sorted) |
// | a move whose set exceeds 8 accounts                     | tooMany(8)                             |
//
// User decisions 2026-10-15: a 1-to-1 call MOVES to a group of everyone in it plus the invitees;
// outsiders are added (channel) or the call moves (group), then rung.
//
// One case the table leaves open, decided here (and in every twin): a DM whose only invitee is the
// DM partner (in the conversation, not in the call) RINGS the partner — there is nothing to move
// to, since a group needs three accounts. It is the "all members → ring" row read for D.

/** Mattermost's group-message ceiling, INCLUDING the reader (PeoplePickerPresentation.maxOthers + 1). */
export const CALL_INVITE_GROUP_LIMIT = 8;

export type CallInvitePlanResult =
  | { kind: 'ring'; invitees: string[] }
  | { kind: 'addThenRing'; toAdd: string[]; ring: string[] }
  | { kind: 'notAllowed'; userIds: string[] }
  | { kind: 'moveToGroup'; ids: string[] }
  | { kind: 'tooMany'; limit: number };

/** Invitees in the order they were picked, without blanks, duplicates or the reader. */
function normalised(invitees: readonly string[], me: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of invitees) {
    if (!id || id === me || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Plain code-unit order: Mattermost ids are lowercase ASCII, so every twin sorts them alike. */
function sortedUnique(ids: Iterable<string>): string[] {
  return [...new Set([...ids].filter(Boolean))].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

function decide(
  channelType: string,
  conversationMemberIds: readonly string[],
  me: string,
  invitees: readonly string[],
  canAddMembers: boolean,
): CallInvitePlanResult {
  const wanted = normalised(invitees, me);
  const members = new Set(conversationMemberIds);
  members.add(me);
  const outsiders = wanted.filter((id) => !members.has(id));

  if (channelType === 'D' || channelType === 'G') {
    if (!outsiders.length) return { kind: 'ring', invitees: wanted };
    const ids = sortedUnique([...members, ...wanted]);
    if (ids.length > CALL_INVITE_GROUP_LIMIT) return { kind: 'tooMany', limit: CALL_INVITE_GROUP_LIMIT };
    return { kind: 'moveToGroup', ids };
  }

  // 'O' / 'P' (and anything unknown, read as a channel: never move a call out of a channel).
  if (!outsiders.length) return { kind: 'ring', invitees: wanted };
  if (!canAddMembers) return { kind: 'notAllowed', userIds: outsiders };
  return { kind: 'addThenRing', toAdd: outsiders, ring: wanted };
}

/**
 * Who the «Invitar» picker offers: people only — never the reader, never anyone already in the
 * call, never a bot account (`is_bot`: it cannot answer a call) and never a deactivated account
 * (`delete_at != 0`: the server would refuse the DM / the membership). Order is kept.
 */
function offered<U extends { id: string; deleteAt: number }>(candidates: readonly U[], excludeIds: ReadonlySet<string>, botIds: ReadonlySet<string>): U[] {
  return candidates.filter((u) => !excludeIds.has(u.id) && !botIds.has(u.id) && u.deleteAt === 0);
}

export const CallInvitePlan = { decide, offered } as const;
